import {
    Box,
    Divider,
    Grid,
    Paper,
    Skeleton,
    Stack,
    styled,
    Tab,
    Tabs,
    Tooltip,
    Typography,
} from "@mui/material";
import ConversationsStore, {
    loadConversationSoundClips,
    loadCreateClip,
    loadCurrentEval,
    loadDeleteClip,
} from "components/Conversations/Stores/ConversationsStore";
import { observer } from "mobx-react";
import clsx from "clsx";
import React, { useMemo, useState } from "react";
import { useStore } from "utils/useStore";
import {
    AcxTranscription,
    InputSearchTranscription,
} from "components/UI/AcxTranscription";
import ConversationClipList from "./components/ConversationClipList";
import ConversationComments from "./components/ConversationComments/ConversationComments";
import AcxToggleButton from "components/UI/AcxToggleButton";
import AcxChatViewer from "components/UI/Chat/AcxChatViewer";
import ConversationSummaryHighlights from "../ConversationSummaryHighlights/ConversationSummaryHighlights";
import {
    ConversationClassifier,
    ConversationService,
} from "services/ConversationService";
import AcxChip from "components/UI/AcxChip";
import {
    AcxLLMReasoning,
    AcxViewLLMReasoningButton,
} from "components/UI/AcxLLMReasoning";
import { useReasoningFeedback } from "components/UI/AcxLLMReasoningFeedback";
import { AuthStore } from "stores/AuthStore";
import theme from "Theme/AppTheme";
import InputSearchChat from "components/UI/Chat/InputSearchChat";
import { AcxTabPanel } from "components/UI/AcxTabPanel";
import { AcxGradientTypography } from "components/UI/AcxGradientTypography";
import { groupBy } from "lodash";
import AcxSelectMulti from "components/UI/Select/BaseSelectComponents/AcxSelectMulti";
import { useSearchParams } from "react-router-dom";
import { ExtractedMetadataList } from "components/UI/ExtractedMetadataList";
import SegmentDropdown from "./components/SegmentDropdown";
import {
    ENGLISH_LANGUAGE_CODE,
    UNREDACTED_ENGLISH_LANGUAGE_CODE,
    getTranslatedLanguageCode,
    getRedactionToggleItems,
    getTranslationToggleItems,
} from "constants/TranscriptLanguageOptions";

const getConversationTabTestId = (label: string) =>
    `focused-conversation-tab-${label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")}`;

const FocusedConversationTabs: React.FunctionComponent = observer(() => {
    const store = useStore(ConversationsStore);

    const [tab, setTab] = React.useState(0);
    const [searchParams, setSearchParams] = useSearchParams();

    React.useEffect(() => {
        const commentIdParam = searchParams.get("commentId");
        if (commentIdParam) {
            setTab(3);
        }
    }, [searchParams]);

    const llmClassifiers = store.selectedConversation?.conversationClassifiers
        .filter((c) => c.classifierType === "LLM")
        .sort((c1, c2) =>
            c1.name
                .toLocaleLowerCase()
                .localeCompare(c2.name.toLocaleLowerCase()),
        );

    const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setTab(newValue);
        if (searchParams.has("commentId")) {
            const nextParams = new URLSearchParams(searchParams);
            nextParams.delete("commentId");
            setSearchParams(nextParams);
        }
    };

    const userCannotViewClips =
        !store.selectedConversation?.hasEval ||
        (store.authStore.canUserView("Restrict Media") &&
            !store.authStore.isUserUltra());

    const conversationTabs = [
        { label: "Highlights", disabled: false, tooltip: "" },
        {
            label: "Transcription",
            disabled: !store.authStore.canUserView("Transcription"),
            tooltip: !store.authStore.canUserView("Transcription")
                ? "Must have permission to view Transcriptions"
                : "",
        },
        {
            label: "Clips",
            disabled: userCannotViewClips,
            tooltip: userCannotViewClips
                ? "Must have an evaluation and media access for clips"
                : "",
        },
        {
            label: "Comments",
        },
        // { label: "Montages", disabled: true, tooltip: "Coming Soon!" },
        {
            label: "Results",
            hidden:
                !store.authStore.canUserView("Model Results") ||
                !store.selectedConversation?.hasLLMClassifierResults,
        },
    ];

    const chatTranslationLanguageCode = getTranslatedLanguageCode(
        store.selectedConversation?.multiLanguageChatTranscription,
    );
    const transcriptTranslationLanguageCode = getTranslatedLanguageCode(
        store.selectedConversation?.multiLangTranscriptions,
    );

    const showChatUnredactedUI =
        store.selectedConversation?.isChat &&
        store.unredactedChatTranscript !== undefined;
    const showChatTranslationUI =
        store.selectedConversation?.isChat &&
        !showChatUnredactedUI &&
        chatTranslationLanguageCode !== undefined;
    const isViewingNonEnglishTranscript =
        store.selectedConversation?.languageCode !== undefined &&
        store.selectedConversation.languageCode !== ENGLISH_LANGUAGE_CODE &&
        store.selectedConversation.languageCode !==
            UNREDACTED_ENGLISH_LANGUAGE_CODE;
    const shouldShowOutlineSegments =
        store.selectedConversation?.languageCode === ENGLISH_LANGUAGE_CODE;
    const isViewingUnredactedTranscript = store.selectedConversation?.languageCode.includes("unredacted");

    return (
        <Grid container pb={2}>
            <Paper square sx={{ width: "100%", borderRadius: "8px" }}>
                <Grid
                    sx={{
                        display: "flex",
                        flexDirection: "row",
                        borderBottom: `2px solid ${theme.palette.lightgray.main}`,
                        mx: 2,
                    }}
                >
                    <Tabs
                        value={tab}
                        onChange={handleChange}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            borderBottom: "none",
                            marginTop: "10px",
                            borderBottomColor: theme.palette.lightgray.main,
                        }}
                    >
                        {conversationTabs
                            .filter((tab) => !tab.hidden)
                            .map((tab, index) => {
                                const tabTestId = getConversationTabTestId(
                                    tab.label,
                                );

                                return (
                                    <Tab
                                        key={`${tab.label}_${index}`}
                                        id={`focused-conversation-tab-${tab.label.toLowerCase()}`}
                                        data-testid={tabTestId}
                                        wrapped
                                        label={
                                            <Tooltip title={tab.tooltip}>
                                                {<span>{tab.label}</span>}
                                            </Tooltip>
                                        }
                                        sx={{
                                            minWidth: "min-content",
                                            textTransform: "none",
                                            fontWeight: 600,
                                            fontSize: "0.85rem",
                                            paddingRight: "20px",
                                        }}
                                        disabled={tab.disabled}
                                        style={{ pointerEvents: "auto" }}
                                    />
                                );
                            })}
                    </Tabs>
                    <Grid
                        item
                        className="pendo-ignore"
                        sx={{
                            float: "right",
                            flexGrow: 1,
                            justifyContent: "flex-end",
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        {store.selectedConversation?.isChat ? (
                            <Grid
                                sx={{
                                    maxWidth: "568px",
                                    position: "relative",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <InputSearchChat
                                    transcriptionMessages={
                                        store.selectedConversation
                                            ?.chatTranscription
                                    }
                                    speakerStore={store.speakerStore}
                                    onResultClick={() => {
                                        setTab(1);
                                    }}
                                />
                            </Grid>
                        ) : (
                            <Grid
                                sx={{
                                    maxWidth: "568px",
                                    position: "relative",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "flex-end",
                                }}
                            >
                                <InputSearchTranscription
                                    speakerStore={store.speakerStore}
                                    onResultClick={(searchResult) => {
                                        setTab(1);
                                        // True sets the actual audio playhead rather than just the number
                                        store.soundClipEditorStore.setPlayHeadTime(
                                            searchResult.startTime,
                                            true,
                                        );
                                    }}
                                />
                            </Grid>
                        )}
                    </Grid>
                </Grid>
                <AcxTabPanel
                    value={tab}
                    index={0}
                    className={clsx("pendo-ignore")}
                >
                    <ConversationSummaryHighlights setTab={setTab} />
                </AcxTabPanel>
                <AcxTabPanel
                    value={tab}
                    index={1}
                    style={{ padding: theme.spacing(3) }}
                >
                    {store.selectedConversation &&
                        (store.selectedConversation.isChat ? (
                            <Box>
                                <Box sx={{ height: `calc(100vh - 275px)` }}>
                                    <AcxChatViewer
                                        records={
                                            store.selectedConversation
                                                .chatTranscription
                                        }
                                        setFocusClip={store.setActiveClip}
                                        mediaClips={store.activeSoundClips}
                                        onClip={store.onTextMediaClipCreated}
                                        eddyEffectP2Data={
                                            store.selectedConversation.eddyData
                                        }
                                        loading={
                                            store.getTaskLoading(
                                                loadCreateClip,
                                            ) ||
                                            store.getTaskLoading(
                                                loadConversationSoundClips,
                                            ) ||
                                            store.getTaskLoading(
                                                loadDeleteClip,
                                            ) ||
                                            store.getTaskLoading(
                                                loadCurrentEval,
                                            )
                                        }
                                        showChatUnredactedUI={
                                            showChatUnredactedUI
                                        }
                                        showChatTranslationUI={
                                            showChatTranslationUI
                                        }
                                        translationLanguageCode={
                                            chatTranslationLanguageCode
                                        }
                                        selectedLanguageCode={
                                            store.selectedConversation
                                                ?.languageCode
                                        }
                                        onChangeLanguage={(value) => {
                                            if (value) {
                                                store.changeTranscriptionLanguage(
                                                    value,
                                                );
                                            }
                                        }}
                                        languageToggleId="focused-conversation-chat-transcript-language-toggle"
                                        alignControlsRight
                                    />
                                </Box>
                            </Box>
                        ) : (
                            <Box>
                                <Box
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        pr: 4,
                                        py: 1,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: "240px",
                                            minHeight: "42px",
                                            flexShrink: 0,
                                        }}
                                    >
                                        {shouldShowOutlineSegments && (
                                            <SegmentDropdown />
                                        )}
                                    </Box>
                                    {transcriptTranslationLanguageCode && (
                                        <AcxToggleButton
                                            exclusive
                                            id="focused-conversation-transcript-lang-toggle"
                                            items={getTranslationToggleItems(
                                                transcriptTranslationLanguageCode,
                                            )}
                                            defaultValue={
                                                store.selectedConversation
                                                    ?.languageCode
                                            }
                                            onChange={(value) => {
                                                if (value) {
                                                    store.changeTranscriptionLanguage(
                                                        value,
                                                    );
                                                }
                                            }}
                                        />
                                    )}
                                    {store.unredactedTranscript && (
                                        <AcxToggleButton
                                            exclusive
                                            id="focused-conversation-transcript-redaction-toggle"
                                            items={getRedactionToggleItems()}
                                            defaultValue={
                                                store.selectedConversation
                                                    ?.languageCode
                                            }
                                            onChange={(value) => {
                                                if (value) {
                                                    store.changeTranscriptionLanguage(
                                                        value,
                                                    );
                                                }
                                            }}
                                        />
                                    )}
                                </Box>
                                {transcriptTranslationLanguageCode &&
                                    !isViewingNonEnglishTranscript && (
                                        <Typography
                                            sx={{
                                                color: theme.palette.gray.main,
                                                fontStyle: "italic",
                                                fontWeight: "bold",
                                                px: 0.5,
                                                pb: 0.5,
                                            }}
                                        >
                                            Translated transcript
                                        </Typography>
                                    )}
                                <Box height={"45vh"}>
                                    <AcxTranscription
                                        focused={tab === 1}
                                        hasEvaluation={
                                            store.selectedConversation?.hasEval
                                        }
                                        audioClip={store.audioClip}
                                        playClip={store.playBlurb}
                                        onClip={store.onAudioClipCreated}
                                        transcriptPhrases={
                                            store.selectedConversation
                                                .audioTranscription?.phrases
                                        }
                                        transcriptWords={
                                            store.selectedConversation
                                                .audioTranscription?.words
                                        }
                                        eddyEffectP2Data={
                                            store.selectedConversation?.eddyData
                                        }
                                        adverseEventData={
                                            store.selectedConversation
                                                ?.adverseEventData
                                        }
                                        conversationSegments={
                                            shouldShowOutlineSegments
                                                ? store.conversationSegments
                                                : undefined
                                        }
                                        minimal
                                        amdId={store.selectedConversationId}
                                        speakerStore={store.speakerStore}
                                        disableClip={
                                            store.authStore.canUserView(
                                                "Restrict Media",
                                            ) && !store.authStore.isUserUltra()
                                        }
                                        disableRedact={
                                            isViewingUnredactedTranscript // You can't do custom redactions unless you're viewing the redacted conversation. This is to prevent the user from trying to redact something the model already caught
                                        }
                                        onTranscriptionStoreLoad={(
                                            newTranscriptionStore,
                                        ) => {
                                            store.setTranscriptionStore(
                                                newTranscriptionStore,
                                            );
                                        }}
                                        onRedactSelection={
                                            store.onRedactSelection
                                        }
                                    />
                                </Box>
                            </Box>
                        ))}
                </AcxTabPanel>
                <AcxTabPanel
                    value={tab}
                    index={2}
                    style={{ padding: theme.spacing(3) }}
                >
                    {store.selectedConversation && <ConversationClipList />}
                </AcxTabPanel>
                <AcxTabPanel
                    value={tab}
                    index={3}
                    style={{ padding: theme.spacing(3) }}
                >
                    <ConversationComments />
                </AcxTabPanel>
                <AcxTabPanel
                    value={tab}
                    index={4}
                    style={{ padding: theme.spacing(3) }}
                >
                    {llmClassifiers && (
                        <LLMClassifierResults classifiers={llmClassifiers} />
                    )}
                </AcxTabPanel>
            </Paper>
        </Grid>
    );
});

const GroupNameTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.gray[700],
    fontSize: "14px",
    fontWeight: 600,
    lineHeight: "20px",
}));

const noGroupSectionName = "No Group";

function LLMClassifierResults(props: {
    classifiers: ConversationClassifier[];
}) {
    const [filteredGroupNames, setFilteredGroupNames] = useState<Set<string>>(
        new Set(),
    );

    if (props.classifiers.length === 0) return null;

    const groups = groupBy(
        props.classifiers,
        (c) => c.groupName ?? noGroupSectionName,
    );
    const groupEntries = Object.entries(groups).sort(([name1], [name2]) => {
        if (name2 === noGroupSectionName) return -1;
        if (name1 === noGroupSectionName) return 1;

        return name1
            .toLocaleLowerCase()
            .localeCompare(name2.toLocaleLowerCase());
    });

    const filterByGroupOptions = groupEntries.map(([name]) => ({
        label: name,
        value: name,
    }));

    const filteredGroupEntries = groupEntries.filter(([name]) => {
        if (filteredGroupNames.size === 0) return true;

        return filteredGroupNames.has(name);
    });

    return (
        <Stack rowGap={2}>
            <Stack direction="row" alignItems="center" columnGap={1}>
                <Typography
                    sx={(theme) => ({
                        color: theme.palette.gray[500],
                        fontSize: "12px",
                        fontWeight: 600,
                        lineHeight: "24px",
                    })}
                >
                    Filter by
                </Typography>
                <AcxSelectMulti
                    id="llm-classifier-groups"
                    options={filterByGroupOptions}
                    labelField="label"
                    valueField="value"
                    defaultValue={filterByGroupOptions.filter((o) =>
                        filteredGroupNames.has(o.value),
                    )}
                    onChange={(options) =>
                        setFilteredGroupNames(
                            new Set(options?.map((o) => o.value)),
                        )
                    }
                    placeholder="Any group"
                    isClearable
                    containerHeight="auto"
                    maxContainerHeight="64px"
                />
            </Stack>
            <Stack rowGap={4}>
                {filteredGroupEntries.map(([groupName, classifiers]) => (
                    <Stack rowGap={1.5} key={groupName}>
                        <GroupNameTypography>{groupName}</GroupNameTypography>
                        {classifiers.map((classifier, idx) => (
                            <React.Fragment key={classifier.classifierId}>
                                <LLMClassifierResult classifier={classifier} />
                                {idx !== classifiers.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </Stack>
                ))}
            </Stack>
        </Stack>
    );
}

function LLMClassifierResult(props: { classifier: ConversationClassifier }) {
    const conversationService = useMemo(() => new ConversationService(), []);
    const authStore = useStore(AuthStore);
    const canViewScoringExplanations = authStore.canUserView(
        "AI Scoring Explanations",
    );

    // conversationId is the AudioMetadataId; both keys come off the classifier.
    const feedback = useReasoningFeedback({
        audioMetadataId: props.classifier.conversationId,
        classifierId: props.classifier.classifierId,
        enabled: canViewScoringExplanations,
    });

    let reasoning: string | undefined = undefined;
    let hasMetadata = false;
    if (!!props.classifier.resultJson) {
        const parsed = JSON.parse(props.classifier.resultJson);
        if ("reasoning" in parsed) reasoning = parsed.reasoning;
        hasMetadata = parsed.writtenExtendedMetadataClassifierIds?.length > 0;
    }

    const [showReasoning, setShowReasoning] = useState(false);
    const [showMetadata, setShowMetadata] = useState(false);
    const [metadataItems, setMetadataItems] = useState<
        ConversationClassifier[] | null
    >(null);
    const [loadingMetadata, setLoadingMetadata] = useState(false);

    const handleToggleMetadata = async () => {
        if (showMetadata) {
            setShowMetadata(false);
            return;
        }

        setShowMetadata(true);

        if (metadataItems !== null) return;

        setLoadingMetadata(true);
        try {
            const items = await conversationService.getExtractedMetadata(
                props.classifier.conversationId,
                props.classifier.classifierId,
            );
            setMetadataItems(items);
        } catch {
            setMetadataItems([]);
        } finally {
            setLoadingMetadata(false);
        }
    };

    return (
        <Stack rowGap={1}>
            <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                columnGap={2}
            >
                <Stack direction="row" alignItems="center" columnGap={1}>
                    <AcxChip
                        size="small"
                        color="lightBlue"
                        label={props.classifier.name}
                        sx={{ margin: 0 }}
                    />
                    {props.classifier.result && (
                        <React.Fragment>
                            <LargeForwardSlash />
                            <AcxChip
                                size="small"
                                color="gray"
                                label={props.classifier.result}
                                sx={{ margin: 0 }}
                            />
                        </React.Fragment>
                    )}
                    {!!props.classifier.description && (
                        <React.Fragment>
                            <LargeForwardSlash />
                            <Typography
                                sx={(theme) => ({
                                    color: theme.palette.gray[700],
                                    fontSize: "13px",
                                    fontWeight: 600,
                                    lineHeight: "20px",
                                })}
                            >
                                {props.classifier.description}
                            </Typography>
                        </React.Fragment>
                    )}
                </Stack>
                <Stack direction="row" columnGap={2} alignItems="center">
                    {canViewScoringExplanations && reasoning && (
                        <AcxViewLLMReasoningButton
                            onClick={() => setShowReasoning((prev) => !prev)}
                        />
                    )}
                    {hasMetadata && (
                        <AcxGradientTypography
                            textAlign="center"
                            role="button"
                            sx={{ cursor: "pointer" }}
                            onClick={handleToggleMetadata}
                        >
                            View Extracted Data
                        </AcxGradientTypography>
                    )}
                </Stack>
            </Stack>
            {canViewScoringExplanations && showReasoning && reasoning && (
                <AcxLLMReasoning
                    reasoning={reasoning}
                    onClose={() => setShowReasoning(false)}
                    feedback={feedback}
                />
            )}
            {showMetadata && loadingMetadata && (
                <Skeleton variant="rounded" height={250} />
            )}
            {showMetadata &&
                !loadingMetadata &&
                metadataItems &&
                metadataItems.length > 0 && (
                    <ExtractedMetadataList
                        items={metadataItems.map((item) => ({
                            key: item.classifierId ?? item.name,
                            name: item.name,
                            value: item.resultJson ?? item.result,
                            jsonSchema: item.resultJsonSchema,
                        }))}
                    />
                )}
        </Stack>
    );
}

function LargeForwardSlash() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="8"
            height="26"
            viewBox="0 0 8 26"
            fill="none"
        >
            <path
                d="M1 25L7 1"
                stroke="#9CA3AF"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export default FocusedConversationTabs;
